import React, { useState } from 'react';
import CurrencyInput from '../components/CurrencyInput';
import { ClientData } from '../types/ClientData';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function ToolFamilyGoals({ data, onUpdate }: Props) {
  const [showChildren, setShowChildren] = useState(
    (data.childMonthlyCommittedCosts || 0) > 0 || (data.childCostsEndAge || 0) > 0
  );

  const handleChildrenToggle = (show: boolean) => {
    setShowChildren(show);
    if (!show) {
      onUpdate({
        childMonthlyCommittedCosts: 0,
        childCostsEndAge: 0,
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* CHILDREN'S COMMITTED COSTS */}
      <div className="rounded-2xl border border-ma-light-gray bg-white p-6">
        <div className="mb-6">
          <h3 className="mb-2 text-lg font-semibold text-ma-navy">
            CHILDREN'S COMMITTED COSTS
          </h3>
          <p className="text-sm text-ma-dark-gray">
            (school fees, university support, regular financial help)
          </p>
        </div>

        <div className="mb-6">
          <label className="mb-3 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
            Do you have any regular financial commitments for children or dependants?
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => handleChildrenToggle(false)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                !showChildren
                  ? 'bg-ma-navy text-white'
                  : 'border border-ma-light-gray bg-white text-ma-dark-gray hover:border-ma-navy'
              }`}
            >
              No
            </button>
            <button
              onClick={() => handleChildrenToggle(true)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                showChildren
                  ? 'bg-ma-navy text-white'
                  : 'border border-ma-light-gray bg-white text-ma-dark-gray hover:border-ma-navy'
              }`}
            >
              Yes
            </button>
          </div>
        </div>

        {showChildren && (
          <div className="space-y-6 rounded-xl border border-ma-light-gray bg-ma-off-white p-4">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
                Total monthly amount
              </label>
              <CurrencyInput
                label="Total monthly amount"
                value={data.childMonthlyCommittedCosts || 0}
                onChange={(value) =>
                  onUpdate({ childMonthlyCommittedCosts: value })
                }
                placeholder="£0.00"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
                These costs end when I am
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={data.childCostsEndAge || 0}
                  onChange={(e) =>
                    onUpdate({
                      childCostsEndAge: parseInt(e.target.value, 10) || 0,
                    })
                  }
                  placeholder="0"
                  className="w-24 rounded-lg border border-ma-light-gray bg-white px-3 py-2 text-sm text-ma-navy placeholder-ma-light-gray focus:border-ma-navy focus:outline-none"
                />
                <span className="text-sm text-ma-dark-gray">years old</span>
              </div>
            </div>

            <div className="rounded-lg bg-ma-light-gray p-3">
              <p className="text-xs leading-relaxed text-ma-dark-gray">
                These reduce your monthly surplus until that age, then redirect
                to your plan. We'll model this in Voyant.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* SESSION INTAKE NOTES */}
      <div className="rounded-2xl border border-ma-light-gray bg-white p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-ma-navy">
            SESSION INTAKE NOTES
          </h3>
        </div>

        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ma-navy">
            Anything specific you'd like to discuss in the session?
          </label>
          <textarea
            value={data.intakeNotes || ''}
            onChange={(e) => onUpdate({ intakeNotes: e.target.value })}
            placeholder="Type here..."
            rows={6}
            className="w-full rounded-lg border border-ma-light-gray bg-white px-3 py-2 text-sm text-ma-navy placeholder-ma-light-gray focus:border-ma-navy focus:outline-none"
          />
          <p className="mt-2 text-xs text-ma-dark-gray">
            (Optional — adviser can also add notes here pre-session)
          </p>
        </div>
      </div>
    </div>
  );
}
